import type { PairedSpectator } from '@/api/types';
import type { StorageLike } from '@/api/tokens';

export const TV_STORAGE_KEY = 'ronateia.tv.v1';

/**
 * O que a TV guarda depois de parear: o token de espectador (só vale nas rotas da TV) e até quando ele vale, em ms no relógio do
 * servidor. A TV não tem conta nem nome, e a partida dela é decidida pelo servidor (na revanche ela muda), então não se guarda
 * qual é: este é o único dado dela neste aparelho.
 */
export interface TvCredentials {
  accessToken: string;
  expiresAt: number;
}

export function credentialsFrom(paired: PairedSpectator): TvCredentials {
  return {
    accessToken: paired.accessToken,
    expiresAt: Date.parse(paired.expiresAt),
  };
}

function isCredentials(value: unknown): value is TvCredentials {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<TvCredentials>;
  return (
    typeof candidate.accessToken === 'string' &&
    candidate.accessToken.length > 0 &&
    Number.isFinite(candidate.expiresAt)
  );
}

/**
 * Guarda o pareamento desta TV no navegador. Sem armazenamento (modo restrito, testes) ele vive só na memória desta aba: a TV
 * continua funcionando, mas pede o código de novo depois de recarregar.
 */
export class TvCredentialsStore {
  private credentials: TvCredentials | null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly storage: StorageLike | null,
    private readonly key: string = TV_STORAGE_KEY,
  ) {
    this.credentials = this.read();
  }

  /** O pareamento atual (a mesma referência até mudar, como o `useSyncExternalStore` pede). */
  readonly get = (): TvCredentials | null => this.credentials;

  save(credentials: TvCredentials): void {
    this.credentials = credentials;
    try {
      this.storage?.setItem(this.key, JSON.stringify(credentials));
    } catch {
      // armazenamento cheio ou bloqueado: o pareamento segue só na memória
    }

    this.emit();
  }

  /** Esquece o pareamento (removida pelo anfitrião, token vencido ou a pessoa desconectou a TV). */
  clear(): void {
    if (this.credentials === null) {
      return;
    }

    this.credentials = null;
    try {
      this.storage?.removeItem(this.key);
    } catch {
      // idem
    }

    this.emit();
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private read(): TvCredentials | null {
    try {
      const raw = this.storage?.getItem(this.key);
      if (!raw) {
        return null;
      }

      const parsed: unknown = JSON.parse(raw);
      return isCredentials(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private emit(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}
